import { useTranslation } from 'react-i18next';
import { Typography } from 'app/components';
import { useEffect, useMemo, useState } from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { TagTable } from '../TagTable/TagTable';
import { TagItemShortcuts } from '../TagItemShortcuts/TagItemShortcuts';

import './Extensions.scss';
import {
  ExtensionConceptSummary,
  TagWithMetadata,
  useDocumentExtensionSchema,
  useDocumentTaxonomyConfig,
  useExtensions,
  useUpdateDocumentExtensionSchema,
  useDeleteExtension,
} from 'entities/tag';
import { NewExtensionModal } from 'app/components/editor/subtoolbar/tags/NewExtension/NewExtensionModal/NewExtensionModal';
import { EditExtensionModal } from 'app/components/editor/subtoolbar/tags/NewExtension/EditExtensionModal/EditExtensionModal';
import { DeleteTagModal } from 'app/components/editor/sidebar/tags/Modal/DeleteTagModal';

const DATA_TYPE_LABELS: Record<string, string> = {
  monetaryItemType: 'Monetary',
  decimalItemType: 'Decimal',
  integerItemType: 'Integer',
  percentItemType: 'Percent',
  sharesItemType: 'Shares',
  stringItemType: 'String',
  textBlockItemType: 'Text block',
  booleanItemType: 'Boolean',
  dateItemType: 'Date',
  gYearItemType: 'Year',
};

const BALANCE_LABELS: Record<string, string> = { debit: 'Debit', credit: 'Credit' };
const PERIOD_LABELS: Record<string, string> = { instant: 'Instant', duration: 'Duration' };

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <Typography variant="body-3" fontWeight="semibold" color="#717077">
        {label}
      </Typography>
      <Typography variant="body-5">{children}</Typography>
    </div>
  );
}

function ExtensionDetailsModal({ extension, onClose }: { extension: ExtensionConceptSummary | null; onClose: () => void }) {
  const { t } = useTranslation(['common', 'tag']);

  if (!extension) return null;
  const ct = extension.conceptType as { baseXbrliType?: string; balance?: string | null; periodType?: string } | undefined;
  const dataType = ct?.baseXbrliType ? DATA_TYPE_LABELS[ct.baseXbrliType] ?? ct.baseXbrliType : null;
  const balance = ct?.balance ? BALANCE_LABELS[ct.balance] ?? ct.balance : '—';
  const period = ct?.periodType ? PERIOD_LABELS[ct.periodType] ?? ct.periodType : null;

  return (
    <BootstrapModal show onHide={onClose} centered>
      <BootstrapModal.Header closeButton>
        <BootstrapModal.Title>{extension.name}</BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body>
        {extension.description && <DetailRow label={t('tag:extension.create.description')}>{extension.description}</DetailRow>}
        <DetailRow label={t('tag:extension.create.wider.anchor')}>{extension.widerAnchor.name}</DetailRow>
        {extension.narrowerAnchors.length > 0 && (
          <DetailRow label={t('tag:extension.create.narrower.anchor')}>
            {extension.narrowerAnchors.map((a, idx) => (
              <span key={a.conceptId}>
                {a.name}
                {idx < extension.narrowerAnchors.length - 1 ? ', ' : ''}
              </span>
            ))}
          </DetailRow>
        )}
        {dataType && <DetailRow label={t('tag:extension.create.data.type')}>{dataType}</DetailRow>}
        <DetailRow label={t('tag:extension.create.balance.type')}>{balance}</DetailRow>
        {period && <DetailRow label={t('tag:extension.create.period.type')}>{period}</DetailRow>}
        <div>
          <Typography variant="body-5" color={extension.tagUsageCount > 0 ? '#15803D' : '#F59E0B'}>
            {extension.tagUsageCount > 0 ? `${extension.tagUsageCount} ${t('tag:instances')}` : t('tag:not.used')}
          </Typography>
        </div>
      </BootstrapModal.Body>
      <BootstrapModal.Footer>
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t('common:close')}
        </button>
      </BootstrapModal.Footer>
    </BootstrapModal>
  );
}

interface ExtensionsProps {
  documentId: string;
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
}

const normalizePrefix = (value: string) => value.trim().toLowerCase();

const normalizeNamespace = (value: string) => {
  const trimmed = value.trim();
  return trimmed && !/^https?:\/\//i.test(trimmed) ? `http://${trimmed}` : trimmed;
};

export const Extensions = ({ documentId }: ExtensionsProps) => {
  const { t } = useTranslation(['common', 'tag']);

  const documentSuperId = parseInt(documentId, 10);
  const validDocumentSuperId = Number.isFinite(documentSuperId) ? documentSuperId : undefined;

  const [sortBy, setSortBy] = useState<'desc' | 'asc'>('asc');
  const [prefixDraft, setPrefixDraft] = useState<string>('');
  const [namespaceDraft, setNamespaceDraft] = useState<string>('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailsExt, setDetailsExt] = useState<ExtensionConceptSummary | null>(null);
  const [editExt, setEditExt] = useState<ExtensionConceptSummary | null>(null);
  const [deleteExt, setDeleteExt] = useState<ExtensionConceptSummary | null>(null);

  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(validDocumentSuperId);
  const selections = taxonomyConfig?.taxonomySelections ?? [];
  const activeTaxonomyId = selections[0]?.taxonomyId;

  const { data: schema } = useDocumentExtensionSchema(validDocumentSuperId);
  const updateSchema = useUpdateDocumentExtensionSchema();

  const { data: extensions } = useExtensions(validDocumentSuperId, activeTaxonomyId);
  const deleteExtension = useDeleteExtension();

  const serverPrefix = schema?.prefix ?? '';
  const serverNamespace = schema?.namespace ?? '';
  useEffect(() => {
    setPrefixDraft(serverPrefix);
    setNamespaceDraft(serverNamespace);
  }, [serverPrefix, serverNamespace]);

  const handleSortToggle = () => {
    setSortBy((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleSaveSchema = () => {
    if (!validDocumentSuperId) return;
    updateSchema.mutate({
      documentSuperId: validDocumentSuperId,
      prefix: normalizePrefix(prefixDraft),
      namespace: normalizeNamespace(namespaceDraft),
    });
  };

  const handleDelete = (ext: ExtensionConceptSummary) => {
    if (!validDocumentSuperId || !activeTaxonomyId) return;
    if (ext.tagUsageCount > 0) {
      window.alert(t('tag:extension.delete.blocked'));
      return;
    }
    setDeleteExt(ext);
  };

  const confirmDelete = () => {
    if (!validDocumentSuperId || !activeTaxonomyId || !deleteExt) return;
    deleteExtension.mutate(
      {
        extensionId: deleteExt.id,
        documentSuperId: validDocumentSuperId,
        taxonomyId: activeTaxonomyId,
      },
      { onSettled: () => setDeleteExt(null) }
    );
  };

  const sortedExtensions = useMemo(
    () =>
      (extensions ?? []).slice().sort((a, b) => {
        return sortBy === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      }),
    [extensions, sortBy]
  );

  const schemaDirty = (schema?.prefix ?? '') !== normalizePrefix(prefixDraft) || (schema?.namespace ?? '') !== normalizeNamespace(namespaceDraft);
  const schemaValid = prefixDraft.trim().length > 0 && namespaceDraft.trim().length > 0;
  const canCreate = !!schema?.prefix && !!schema?.namespace && !!activeTaxonomyId;

  // Backend stores `name` inconsistently (some records carry the prefix,
  // some are bare). Normalize to a single qualified form for display so the
  // column matches the EXTENDS column's `ifrs-full:…` style.
  const qualifiedName = (name: string) => {
    const local = name.includes(':') ? name.slice(name.indexOf(':') + 1) : name;
    return serverPrefix ? `${serverPrefix}:${local}` : local;
  };

  const tagCategories = [t('tag:name'), t('tag:extends'), t('tag:active')];

  return (
    <div className="tagging-extensions">
      <Typography variant="body-8" fontWeight="semibold">
        {t('tag:extension.settings')}
      </Typography>

      <section className="tagging-extensions__taxonomy-form">
        <div>
          <Typography variant="body-7" fontWeight="semibold" className="mb-2">
            {t('tag:prefix.label')}
          </Typography>
          <input
            className="tagging-extensions__input tagging-extensions__input"
            type="text"
            value={prefixDraft}
            onChange={(e) => setPrefixDraft(e.target.value)}
            placeholder={t('tag:prefix.placeholder')}
          />
        </div>

        <div>
          <Typography variant="body-7" fontWeight="semibold" className="mb-2">
            {t('tag:namespace.label')}
          </Typography>
          <input
            className="tagging-extensions__input tagging-extensions__input--large"
            type="text"
            value={namespaceDraft}
            onChange={(e) => setNamespaceDraft(e.target.value)}
            placeholder={t('tag:namespace.placeholder')}
          />
        </div>

        <button className="btn btn-primary" onClick={handleSaveSchema} disabled={!schemaDirty || !schemaValid || updateSchema.isPending}>
          {t('common:save')}
        </button>
      </section>

      <Typography variant="body-8" fontWeight="semibold">
        {t('tag:taxonomy.extensions')}
      </Typography>

      <button className="btn btn-primary" onClick={() => setCreateModalOpen(true)} disabled={!canCreate}>
        {t('tag:create.extension')}
      </button>

      <TagTable categories={tagCategories} setSortBy={handleSortToggle} sortBy={sortBy}>
        {sortedExtensions.map((ext) => (
          <tr key={ext.id}>
            <td>
              <Typography variant="body-5" fontWeight="semibold" color="#37353B">
                {qualifiedName(ext.name)}
              </Typography>
              {ext.description && (
                <Typography variant="body-3" fontWeight="light">
                  {ext.description}
                </Typography>
              )}
            </td>
            <td>
              <Typography variant="body-5" fontWeight="semibold" color="#6B7076">
                {ext.widerAnchor.name}
              </Typography>
            </td>
            <td>
              <Typography variant="body-5" fontWeight="semibold" color={ext.tagUsageCount > 0 ? '#15803D' : '#F59E0B'}>
                {ext.tagUsageCount > 0 ? `${ext.tagUsageCount} ${t('tag:instances')}` : t('tag:not.used')}
              </Typography>
            </td>
            <td>
              <TagItemShortcuts
                actions={[
                  { type: 'details', onClick: () => setDetailsExt(ext) },
                  { type: 'edit', onClick: () => setEditExt(ext) },
                  { type: 'delete', onClick: () => handleDelete(ext) },
                ]}
              />
            </td>
          </tr>
        ))}
      </TagTable>

      {validDocumentSuperId && activeTaxonomyId && (
        <NewExtensionModal
          show={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          documentSuperId={validDocumentSuperId}
          taxonomyId={activeTaxonomyId}
        />
      )}
      <ExtensionDetailsModal extension={detailsExt} onClose={() => setDetailsExt(null)} />
      <DeleteTagModal
        show={!!deleteExt}
        busy={deleteExtension.isPending}
        message={t('tag:extension.delete.confirm')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteExt(null)}
      />
      {validDocumentSuperId && activeTaxonomyId && editExt && (
        <EditExtensionModal
          show={!!editExt}
          onClose={() => setEditExt(null)}
          documentSuperId={validDocumentSuperId}
          taxonomyId={activeTaxonomyId}
          extension={editExt}
        />
      )}
    </div>
  );
};
